import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiGrid, FiUsers, FiBell, FiCalendar, FiImage, FiAward, FiMessageCircle,
  FiLogOut, FiMenu, FiX, FiChevronRight, FiUser, FiSettings, FiTruck,
  FiShoppingBag, FiUserCheck, FiHome, FiUserPlus, FiClock
} from 'react-icons/fi';
import { MdSchool } from 'react-icons/md';
import { useAuth } from '../../context/AuthContext';
import { contactAPI } from '../../services/api';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { ROLE_LABELS } from '../../utils/roles';

const WEB_ROLES = ['admin', 'web_management'];
const STORE_ROLES = ['admin', 'store_management'];

const navItems = [
  { label: 'Dashboard', path: '/admin', icon: FiGrid, exact: true, roles: ['admin'] },
  { label: 'Admissions', path: '/admin/admissions', icon: FiUsers, roles: WEB_ROLES },
  { label: 'Notices', path: '/admin/notices', icon: FiBell, roles: WEB_ROLES },
  { label: 'Events', path: '/admin/events', icon: FiCalendar, roles: WEB_ROLES },
  { label: 'Gallery', path: '/admin/gallery', icon: FiImage, roles: WEB_ROLES },
  { label: 'Results', path: '/admin/results', icon: FiAward, roles: WEB_ROLES },
  { label: 'Contact Messages', path: '/admin/contacts', icon: FiMessageCircle, roles: WEB_ROLES },
  { label: 'Bus Management', path: '/admin/buses', icon: FiTruck, roles: STORE_ROLES },
  { label: 'Store Management', path: '/admin/store', icon: FiShoppingBag, roles: STORE_ROLES },
  { label: 'Staff Attendance', path: '/admin/staff-attendance', icon: FiUserCheck, roles: STORE_ROLES },
  { label: 'Infrastructure', path: '/admin/infrastructure', icon: FiHome, roles: STORE_ROLES },
  { label: 'Staff Accounts', path: '/admin/staff-accounts', icon: FiUserPlus, roles: ['admin'] },
  { label: 'Login Activity', path: '/admin/login-history', icon: FiClock, roles: ['admin'] },
  { label: 'Site Settings', path: '/admin/settings', icon: FiSettings, roles: ['admin'] },
];

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadContacts, setUnreadContacts] = useState(0);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { settings } = useSiteSettings();

  const role = user?.role || 'admin';
  const visibleNavItems = navItems.filter((item) => item.roles.includes(role));
  const roleLabel = ROLE_LABELS[role] || 'Admin';

  useEffect(() => {
    if (role !== 'admin' && role !== 'web_management') return;
    contactAPI.getAll().then((res) => {
      setUnreadContacts(res.data?.data?.unreadCount || 0);
    }).catch(() => {});
  }, [role]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0 border border-white/30">
            <MdSchool className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="font-bold text-white text-sm tracking-wide leading-tight">{settings?.schoolName || "School Admin"}</h2>
            <p className="text-blue-200 text-[10px] uppercase tracking-widest mt-0.5">{roleLabel} Panel</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {visibleNavItems.map((item, idx) => (
          <NavLink
            key={idx}
            to={item.path}
            end={item.exact}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-sm font-semibold group ${
                isActive
                  ? 'bg-white text-[#1D4ED8] shadow-md'
                  : 'text-blue-100 hover:bg-white/10 hover:text-white'
              }`
            }
            onClick={() => setSidebarOpen(false)}
          >
            <item.icon className="w-4.5 h-4.5 flex-shrink-0" />
            {item.label}
            {item.path === '/admin/contacts' && unreadContacts > 0 && (
              <span className="ml-auto bg-sky-400 text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0">
                {unreadContacts > 9 ? '9+' : unreadContacts}
              </span>
            )}
            {item.path !== '/admin/contacts' && (
              <FiChevronRight className="w-3.5 h-3.5 ml-auto opacity-0 group-hover:opacity-60 transition-opacity" />
            )}
          </NavLink>
        ))}
      </nav>

      {/* User Info */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 px-4 py-3 bg-white/10 rounded-xl mb-2">
          <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center border border-white/20">
            <FiUser className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-xs font-semibold truncate">{user?.name || 'Admin'}</p>
            <p className="text-blue-200/60 text-[10px] truncate">{user?.email || 'admin@school.com'}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-4 py-3 text-red-300 hover:bg-red-500/20 hover:text-red-200 transition-all rounded-xl text-xs font-semibold"
        >
          <FiLogOut className="w-4 h-4" />
          Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 flex-shrink-0" style={{ background: 'linear-gradient(180deg, #1e3a8a 0%, #1D4ED8 100%)' }}>
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-slate-900/60 z-40 backdrop-blur-sm"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', damping: 25 }}
              className="lg:hidden fixed left-0 top-0 bottom-0 w-64 z-50 flex flex-col"
              style={{ background: 'linear-gradient(180deg, #1e3a8a 0%, #1D4ED8 100%)' }}
            >
              <SidebarContent />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Header */}
        <header className="bg-white border-b border-blue-100 px-6 py-4 flex items-center justify-between flex-shrink-0 shadow-sm">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden p-2 text-slate-500 hover:text-blue-700 hover:bg-blue-50 rounded-xl transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <FiMenu className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-bold text-[#1e3a8a] text-lg">{roleLabel} Dashboard</h1>
              <p className="text-slate-500 text-xs hidden sm:block">
                Welcome back, {user?.name || 'Admin'}
              </p>
            </div>
          </div>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold shadow-md"
            style={{ background: 'linear-gradient(135deg, #1D4ED8, #0ea5e9)' }}>
            {user?.name
              ? user.name.trim().split(/\s+/).slice(0, 2).map(w => w[0].toUpperCase()).join('')
              : 'A'}
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            className="w-full h-full"
          >
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
